import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

export interface MailboxReplyData {
  email: string
  from_name: string
  client_name: string
  domain: string
  replies_today: number
  replies_7d: number
  replies_14d: number
  reply_rate: number
  last_reply_time: string | null
  total_replies: number
}

export function useMailboxReplyAnalytics() {
  return useQuery({
    queryKey: ['mailbox-reply-analytics'],
    queryFn: async (): Promise<MailboxReplyData[]> => {
      return await fetchReplyAnalyticsManually()
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  })
}

async function fetchReplyAnalyticsManually(): Promise<MailboxReplyData[]> {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  
  const fourteenDaysAgo = new Date(today)
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14)

  // Get reply events for different time periods
  const { data: replyEvents, error: replyError } = await supabase
    .from('obsync_dynamic_reports')
    .select('from_email, created_at, event_type')
    .eq('event_type', 'EMAIL_REPLY')
    .gte('created_at', fourteenDaysAgo.toISOString())
    .order('created_at', { ascending: false })

  if (replyError) {
    console.error('Error fetching reply events:', replyError)
    return []
  }

  // Get mailbox data - no join since there's no relation
  const { data: mailboxes, error: mailboxError } = await supabase
    .from('mailboxes')
    .select('email, from_name, domain, client_id')

  if (mailboxError) {
    console.error('Error fetching mailboxes:', mailboxError)
    return []
  }

  // Process reply data by mailbox
  const repliesByMailbox = new Map<string, {
    today: number
    last7d: number
    last14d: number
    lastReplyTime: string | null
    totalReplies: number
  }>()

  replyEvents?.forEach(event => {
    if (!event.from_email) return
    
    const eventDate = new Date(event.created_at)
    const email = event.from_email.toLowerCase()
    
    if (!repliesByMailbox.has(email)) {
      repliesByMailbox.set(email, {
        today: 0,
        last7d: 0,
        last14d: 0,
        lastReplyTime: null,
        totalReplies: 0
      })
    }
    
    const replyData = repliesByMailbox.get(email)!
    
    // Count total replies (all replies regardless of time)
    replyData.totalReplies++
    
    // Count replies for different periods
    if (eventDate >= today) {
      replyData.today++
    }
    if (eventDate >= sevenDaysAgo) {
      replyData.last7d++
    }
    if (eventDate >= fourteenDaysAgo) {
      replyData.last14d++
    }
    
    // Track most recent reply
    if (!replyData.lastReplyTime || eventDate > new Date(replyData.lastReplyTime)) {
      replyData.lastReplyTime = event.created_at
    }
  })

  // Combine mailbox data with reply analytics
  const result: MailboxReplyData[] = []
  
  mailboxes?.forEach(mailbox => {
    const replyData = repliesByMailbox.get(mailbox.email.toLowerCase())
    
    // Only include mailboxes that have replies today or recently
    if (replyData && (replyData.today > 0 || replyData.last7d > 0)) {
      result.push({
        email: mailbox.email,
        from_name: mailbox.from_name || '',
        client_name: (mailbox as any).client_id?.toString() || '',
        domain: mailbox.domain,
        replies_today: replyData.today,
        replies_7d: replyData.last7d,
        replies_14d: replyData.last14d,
        reply_rate: 0, // Calculate from reply events
        last_reply_time: replyData.lastReplyTime,
        total_replies: replyData.totalReplies
      })
    }
  })

  // Sort by total replies (highest first), then by today's replies, then by 7-day replies
  return result.sort((a, b) => {
    if (a.total_replies !== b.total_replies) {
      return b.total_replies - a.total_replies
    }
    if (a.replies_today !== b.replies_today) {
      return b.replies_today - a.replies_today
    }
    return b.replies_7d - a.replies_7d
  })
}